/**
 * prompts.js - turning a support rung into something Pip can say and the
 * screen can show.
 *
 * PURE. The words come from content/voice.js; which words, from the engine's
 * rung. Line SELECTION is injected (`choose`) so the anti-repeat bag stays in
 * one place and this file stays testable.
 *
 * ---------------------------------------------------------------------------
 * THE RUNG DECIDES WHAT MAY BE SAID
 *
 * Section 8.2 gives each phase a prompt style and says what it MUST include -
 * "a Phase 1 stage that does not have Pip speak the word aloud is not a Phase 1
 * stage". The rung refines that within the trial. The rule this file enforces
 * is simply that a rung which is supposed to withhold the answer withholds it:
 * the visual and gestural rungs draw from templates that contain no {word} and
 * no {phrase}, which validateContent() asserts.
 */

import { PROMPTS, HINTS, renderLine } from '../content/voice.js';
import { getObject, getCarrier, canonicalPhrase, CARRIER_IDS } from '../content/objects.js';
import { rungIndexOf, rungIdAt } from '../../shared/support.js';

/**
 * Templates for a rung, falling back toward MORE support when a phase does not
 * define the exact rung.
 *
 * Falling back toward more support is the safe direction: the worst case is
 * that the child is given more help than the rung intended, which costs a box
 * (the record still says which rung was live) rather than leaving Pip mute.
 */
export function templatesFor(phase, rungId) {
  const byRung = PROMPTS[phase] || {};
  if (byRung[rungId] && byRung[rungId].length) return byRung[rungId];

  for (let index = rungIndexOf(rungId) - 1; index >= 0; index -= 1) {
    const id = rungIdAt(index);
    if (byRung[id] && byRung[id].length) return byRung[id];
  }

  const any = Object.values(byRung).find((lines) => lines && lines.length);
  return any || ['Your turn.'];
}

/**
 * What is drawn beside the prompt at this rung.
 *
 *   'word'          the whole word, because Pip just said it
 *   'partial_word'  "A ___" - the spec's Phase 2 Level 1
 *   'first_letter'  "A"     - the spec's Phase 2 Level 2
 *   null            nothing; the picture is the only cue
 *
 * @returns {{kind:string, text:string}|null}
 */
export function cueFor({ rungId, cueStyle, objectId }) {
  const object = getObject(objectId);
  if (!object) return null;

  if (rungId === 'full_model') return { kind: 'word', text: object.word };

  if (rungId === 'partial_verbal') {
    if (cueStyle === 'partial_word') {
      const head = object.firstLetter || object.word[0].toUpperCase();
      return { kind: 'partial_word', text: `${head}${'_'.repeat(Math.max(2, object.word.length - head.length))}` };
    }
    if (cueStyle === 'first_letter') {
      return { kind: 'first_letter', text: object.firstLetter || object.word[0].toUpperCase() };
    }
  }

  return null;
}

/**
 * Build one prompt.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {string} input.rungId
 * @param {{objectId:string, carrierId:string|null}} input.target
 * @param {object} input.content            the level's content entry
 * @param {(templates:string[]) => string} input.choose
 * @returns {{text:string, cue:object|null, showsAnswer:boolean,
 *            chips:Array<{id:string,label:string,art:string}>|null}}
 */
export function buildPrompt({ phase, rungId, target, content, choose }) {
  const object = getObject(target.objectId);
  const carrier = target.carrierId ? getCarrier(target.carrierId) : null;

  const text = renderLine(choose(templatesFor(phase, rungId)), {
    word: object ? object.word : '',
    phrase: canonicalPhrase(target),
    letter: object ? (object.firstLetter || object.word[0].toUpperCase()) : '',
    carrier: carrier ? carrier.word : '',
    carriers: (content.carriers || CARRIER_IDS).map((id) => getCarrier(id).word).join(' or '),
    clue: object ? object.clue : '',
  });

  return {
    text,
    cue: cueFor({ rungId, cueStyle: content.cueStyle, objectId: target.objectId }),
    // Does this prompt hand the child the answer? Drives the trial record's
    // modelPlayed, which gates Phase 2 B and C and Phase 6 D.
    showsAnswer: rungId === 'full_model',
    // Section 8.2, phase 3: "Word frames, object choices". Hidden at the
    // gestural rung, where the whole point is that Pip only points.
    chips: phase === 3 && rungId !== 'gestural'
      ? (content.carriers || []).map((id) => {
          const c = getCarrier(id);
          return { id, label: c.label, art: c.art };
        })
      : null,
  };
}

/** Pip's semantic clue for this level and target. */
export function buildHint({ phase, target, content, choose }) {
  const object = getObject(target.objectId);
  const carrier = target.carrierId ? getCarrier(target.carrierId) : null;
  return renderLine(choose(HINTS[phase] || HINTS[1]), {
    clue: object ? object.clue : '',
    carrier: carrier ? carrier.word : '',
    carriers: (content.carriers || CARRIER_IDS).map((id) => getCarrier(id).word).join(' or '),
    word: object ? object.word : '',
  });
}

export default { templatesFor, cueFor, buildPrompt, buildHint };
